import { RefObject, useCallback, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryTypeKey } from '../../../../../containers/EmbroideryContainer/combination-rules/types'

/** The types DTC has an editor for. Opening anything else would name a view that cannot be drawn. */
const EDITABLE_TYPE_KEYS: readonly EmbroideryTypeKey[] = ['text', 'icon', 'logo']

export type DtcSectionNavigation = {
  openTypeKey: EmbroideryTypeKey | null
  openType(typeKey: EmbroideryTypeKey): void
  goBack: (() => void) | undefined
  headerTitle: string
  /** For the heading that shows `headerTitle`. It takes focus whenever the view changes. */
  headerTitleRef: RefObject<HTMLHeadingElement | null>
}

/**
 * Which of the DTC panel's views is on top.
 *
 * No provider models this — it is the modal's own state — and it is deliberately not held inside
 * the panel, because the mobile header names the open view and carries its back control from
 * outside the panel, and needs the same answer. The composition root calls this and hands each
 * half what it needs.
 *
 * @param initialTypeKey - The editor to start on, or `null` for the option list.
 */
export function useDtcSectionNavigation(
  initialTypeKey: EmbroideryTypeKey | null = null
): DtcSectionNavigation {
  const [openTypeKey, setOpenTypeKey] = useState<EmbroideryTypeKey | null>(() =>
    initialTypeKey !== null && EDITABLE_TYPE_KEYS.includes(initialTypeKey) ? initialTypeKey : null
  )
  const {
    embroidery: {
      modal: {
        title: optionsTitle,
        forms: { titles },
      },
    },
  } = useTranslation()

  const headerTitleRef = useRef<HTMLHeadingElement>(null)

  // Swapping views unmounts the control the customer used, so focus moves to the new view's title.
  // flushSync renders the new view first, so the title is already the new one when it takes focus.
  const showView = useCallback((typeKey: EmbroideryTypeKey | null) => {
    flushSync(() => setOpenTypeKey(typeKey))
    headerTitleRef.current?.focus()
  }, [])

  const openType = useCallback(
    (typeKey: EmbroideryTypeKey) => {
      if (EDITABLE_TYPE_KEYS.includes(typeKey)) showView(typeKey)
    },
    [showView]
  )
  const goToOptions = useCallback(() => showView(null), [showView])

  return {
    openTypeKey,
    openType,
    goBack: openTypeKey === null ? undefined : goToOptions,
    headerTitle: openTypeKey === null ? optionsTitle : titles[openTypeKey],
    headerTitleRef,
  }
}
